import { useLayoutEffect, useState } from 'react';
import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import styles from './OrgItem.module.scss';
import { withStyles, Box, MaterialSymbol, CircleButton } from 'vopay';
import OrgDetails from '../OrgDetails/OrgDetails';

import * as Popover from '@radix-ui/react-popover';

const classes = withStyles(styles);

interface OrgItemProps {
  node: ExtendedNestedSubAccount;
  clientAccountsEnabled: boolean;
  onToggleExpand: (node: ExtendedNestedSubAccount) => void;
  onChangeMenuState: (open: boolean, node: ExtendedNestedSubAccount) => void;
  openCreateSubAccount: (node: ExtendedNestedSubAccount) => void;
  openAccountTransfer: (node: ExtendedNestedSubAccount) => void;
  onLocationChange: (location: string, state?: unknown) => void;
}

const OrgItem: React.FC<OrgItemProps> = ({
  node,
  clientAccountsEnabled,
  onToggleExpand,
  onChangeMenuState,
  openAccountTransfer,
  openCreateSubAccount,
  onLocationChange,
}) => {
  const { isExpanded, AccountName, totalAccounts, AccountStatus, isOpen, isBlurred } = node;
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      setOpen(isOpen);
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [isOpen]);

  return (
    <Popover.Root open={open}>
      <Popover.Trigger asChild>
        <div
          className={classes(
            'container',
            isExpanded && 'expanded',
            isOpen && 'active',
            isBlurred && 'blur',
            totalAccounts !== 0 && 'hasChildren',
          )}
          onClick={() => {
            if (totalAccounts !== 0) onToggleExpand(node);
          }}
        >
          <div className={classes('overlay')} />
          <div className={classes('overlayBlur')} />
          <div className={classes('orgItemBar', AccountStatus === 'Active' && 'active')}></div>
          <div className={classes('orgItem')}>
            <Box flx="row" jc="fe" gapSize="4px">
              {node.AccountStatus === 'Active' && (
                <CircleButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openCreateSubAccount(node);
                  }}
                  icon="add"
                  size="small"
                  colorScheme="action"
                  className={classes('orgHoverWrapper', 'addButton')}
                />
              )}
              <CircleButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChangeMenuState(true, node);
                }}
                icon="more_horiz"
                size="small"
                colorScheme="action"
                className={classes('orgHoverWrapper')}
              />
            </Box>
            <Box flx="row" jc="center" className={classes('orgItemTitle')}>
              {AccountName}
            </Box>
            <Box flx="row" jc="fe" maxheight="18px">
              {totalAccounts !== 0 && (
                <button className={classes('orgItemExpand')} onClick={() => onToggleExpand(node)}>
                  {totalAccounts}{' '}
                  {isExpanded ? (
                    <MaterialSymbol icon="keyboard_arrow_up" />
                  ) : (
                    <MaterialSymbol icon="keyboard_arrow_down" />
                  )}
                </button>
              )}
            </Box>
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={'right'}
          align={'end'}
          collisionPadding={{
            top: 75,
          }}
          sideOffset={15}
          data-popover-type="orgDetails"
        >
          <OrgDetails
            node={node}
            onClose={() => onChangeMenuState(false, node)}
            onLocationChange={onLocationChange}
            clientAccountsEnabled={clientAccountsEnabled}
            onAccountTransfer={() => {
              openAccountTransfer(node);
            }}
            onCreateSubAccount={() => {
              openCreateSubAccount(node);
            }}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default OrgItem;
